import React from "react";
import DropdownSelect from "../../../assets/reusableComponents/dropDownSelect";
import { IoIosArrowRoundDown } from "react-icons/io";

export default function IncomeDetailsPage({
  selectedIncomeCard,
  incomeDetailMonth,
  setIncomeDetailMonth,
  incomeDetailYear,
  setIncomeDetailYear,
  incomeDetailYearChoices,
  selectedIncomeApartment,
  setSelectedIncomeApartment,
  properties,
}) {
  const [expandedTransaction, setExpandedTransaction] = React.useState(null);
  const [hoveredTrendIndex, setHoveredTrendIndex] = React.useState(null);

  const propertyDropdownChoices = Array.isArray(properties)
    ? ["All properties", ...properties.map(p => p.property_name || p.name)]
    : ["All properties"];

  const handleExportMonthly = () => {
    let targetProperty = null;
    if (Array.isArray(properties) && properties.length > 0) {
      if (selectedIncomeApartment && selectedIncomeApartment !== "All properties") {
        targetProperty = properties.find(p => (p.property_name || p.name) === selectedIncomeApartment);
      }
      if (!targetProperty) {
        targetProperty = properties[0];
      }
    }

    const currentPropertyId = targetProperty?.property_id || targetProperty?._id || null;

    if (!currentPropertyId) {
      alert("No property selected/found. Please ensure you have properties.");
      return;
    }

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const monthIndex = monthNames.indexOf(incomeDetailMonth) + 1; // 1 to 12
    const year = incomeDetailYear;

    try {
      const baseUrl = process.env.REACT_APP_ENDPOINT;
      const url = `${baseUrl}/reports/export/month?propertyId=${currentPropertyId}&month=${monthIndex}&year=${year}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error("Export error", error);
      alert("Error exporting report");
    }
  };

  const apartmentNames = [
    "Sunrise 2B",
    "Block C - 12",
    "Oakview 4A",
    "Maple 1C",
    "Riverpark A3",
  ];

  const selectedPropertyIndex = propertyDropdownChoices.indexOf(selectedIncomeApartment);
  const activeMockApartment = selectedPropertyIndex > 0
    ? apartmentNames[(selectedPropertyIndex - 1) % apartmentNames.length]
    : "All apartments";

  const incomeWeightsByCard = {
    "Rent Collected": [0.27, 0.21, 0.18, 0.19, 0.15],
    "Garbage Paid": [0.22, 0.18, 0.24, 0.21, 0.15],
    "Water Paid": [0.24, 0.17, 0.22, 0.2, 0.17],
    "Arrears Paid": [0.2, 0.23, 0.16, 0.24, 0.17],
    "Deposit Paid": [0.28, 0.2, 0.19, 0.17, 0.16],
    "Other Income": [0.19, 0.17, 0.22, 0.2, 0.22],
  };

  const monthPatternsByCard = {
    "Rent Collected": [
      0.88, 0.92, 0.96, 1.01, 1.05, 1.1, 1.04, 1.08, 1.12, 1.16, 1.09, 1.2,
    ],
    "Garbage Paid": [
      0.76, 0.83, 0.9, 0.94, 0.98, 1.05, 0.97, 1.0, 1.03, 1.07, 1.02, 1.09,
    ],
    "Water Paid": [
      0.79, 0.85, 0.91, 0.96, 1.02, 1.07, 1.0, 1.03, 1.09, 1.11, 1.04, 1.14,
    ],
    "Arrears Paid": [
      0.66, 0.74, 0.82, 0.91, 0.95, 1.02, 0.96, 1.01, 1.08, 1.14, 1.06, 1.21,
    ],
    "Deposit Paid": [
      0.81, 0.86, 0.92, 0.95, 0.99, 1.04, 0.98, 1.02, 1.07, 1.09, 1.05, 1.12,
    ],
    "Other Income": [
      0.69, 0.73, 0.79, 0.86, 0.91, 0.96, 0.89, 0.95, 1.0, 1.04, 1.01, 1.08,
    ],
  };

  const chartMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const chartMonthsLong = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentYear = new Date().getFullYear();
  const yearChoices =
    incomeDetailYearChoices && incomeDetailYearChoices.length
      ? incomeDetailYearChoices.map((year) => String(year))
      : [String(currentYear), String(currentYear - 1), String(currentYear - 2)];

  const formatAmount = (value) =>
    Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const formatShortAmount = (value) => {
    const num = Number(value || 0);
    const abs = Math.abs(num);
    if (abs >= 1000000) {
      return `${Number((num / 1000000).toFixed(1)).toString()}m`;
    }
    if (abs >= 1000) {
      return `${Number((num / 1000).toFixed(1)).toString()}k`;
    }
    return formatAmount(num);
  };

  const createIncomeDetailData = (card, years) => {
    if (!card) return null;

    const weights =
      incomeWeightsByCard[card.title] || incomeWeightsByCard["Other Income"];
    const pattern =
      monthPatternsByCard[card.title] || monthPatternsByCard["Other Income"];

    const monthlyTotals = pattern.map((factor) =>
      Math.round((card.amount || 0) * factor),
    );

    const apartments = apartmentNames.map((name, apartmentIndex) => ({
      name,
      monthlyByYear: years.reduce((accumulator, yearChoice, yearIndex) => {
        const parsedYear = Number(yearChoice);
        const yearsBack = Number.isFinite(parsedYear)
          ? currentYear - parsedYear
          : yearIndex;
        const yearFactor = Math.max(0.72, 1 - yearsBack * 0.06);

        accumulator[yearChoice] = monthlyTotals.map(
          (monthTotal, monthIndex) => {
            const variability =
              1 + ((apartmentIndex + monthIndex) % 3 === 0 ? 0.03 : -0.02);
            return Math.max(
              0,
              Math.round(
                monthTotal * weights[apartmentIndex] * variability * yearFactor,
              ),
            );
          },
        );

        return accumulator;
      }, {}),
    }));

    return {
      months: chartMonths,
      apartments,
    };
  };

  const detailData = createIncomeDetailData(selectedIncomeCard, yearChoices);
  const selectedMonthIndexRaw = chartMonthsLong.indexOf(incomeDetailMonth);
  const selectedMonthIndex =
    selectedMonthIndexRaw >= 0 ? selectedMonthIndexRaw : new Date().getMonth();
  const selectedYear = yearChoices.includes(String(incomeDetailYear))
    ? String(incomeDetailYear)
    : yearChoices[0];
  const selectedPeriodLabel = `${chartMonthsLong[selectedMonthIndex]} ${selectedYear}`;
  const daysInSelectedMonth = new Date(
    Number(selectedYear),
    selectedMonthIndex + 1,
    0,
  ).getDate();
  const visibleDays = Array.from(
    { length: Math.max(daysInSelectedMonth, 1) },
    (_, index) => index + 1,
  );
  const weekScaleLabels = Array.from(
    { length: Math.ceil(daysInSelectedMonth / 7) },
    (_, index) => {
      const startDay = index * 7 + 1;
      const endDay = Math.min(startDay + 6, daysInSelectedMonth);

      return {
        key: `week-${index + 1}`,
        label: `Week ${index + 1}`,
        span: endDay - startDay + 1,
        startDay,
        endDay,
      };
    },
  );

  const detailApartmentChoices = detailData
    ? ["All apartments", ...detailData.apartments.map((item) => item.name)]
    : ["All apartments"];

  const buildDailyBreakdown = (monthlyAmount, apartmentIndex) => {
    if (monthlyAmount <= 0) {
      return Array.from({ length: daysInSelectedMonth }, () => 0);
    }

    const rawWeights = Array.from(
      { length: daysInSelectedMonth },
      (_, index) => {
        const dayNumber = index + 1;
        const weekDay = new Date(
          Number(selectedYear),
          selectedMonthIndex,
          dayNumber,
        ).getDay();
        const weekDayFactor = weekDay === 0 || weekDay === 6 ? 0.9 : 1.1;
        const pulse =
          ((index + apartmentIndex + selectedMonthIndex) % 6) * 0.035;
        return weekDayFactor + pulse;
      },
    );

    const totalWeight = rawWeights.reduce((sum, weight) => sum + weight, 0);
    const exactDistribution = rawWeights.map(
      (weight) => (monthlyAmount * weight) / totalWeight,
    );
    const baseDistribution = exactDistribution.map((value) =>
      Math.floor(value),
    );
    let remainder =
      monthlyAmount - baseDistribution.reduce((sum, value) => sum + value, 0);

    const remainderPriority = exactDistribution
      .map((value, index) => ({
        index,
        fraction: value - Math.floor(value),
      }))
      .sort((a, b) => b.fraction - a.fraction);

    let pointer = 0;
    while (remainder > 0 && remainderPriority.length > 0) {
      const targetIndex =
        remainderPriority[pointer % remainderPriority.length].index;
      baseDistribution[targetIndex] += 1;
      remainder -= 1;
      pointer += 1;
    }

    return baseDistribution;
  };

  const visibleApartmentSeries = detailData
    ? detailData.apartments.map((apartment, apartmentIndex) => {
        const monthlyAmount =
          apartment.monthlyByYear?.[selectedYear]?.[selectedMonthIndex] || 0;

        return {
          ...apartment,
          monthly: [monthlyAmount],
          daily: buildDailyBreakdown(monthlyAmount, apartmentIndex),
        };
      })
    : [];

  const trendSeries =
    activeMockApartment === "All apartments"
      ? visibleDays.map((_, index) =>
          visibleApartmentSeries.reduce(
            (sum, apartment) => sum + (apartment.daily?.[index] || 0),
            0,
          ),
        )
      : visibleApartmentSeries.find(
          (apartment) => apartment.name === activeMockApartment,
        )?.daily || [];

  const distributionRows = visibleApartmentSeries
    .map((apartment) => {
      const total = apartment.monthly.reduce((sum, value) => sum + value, 0);
      return {
        apartment: apartment.name,
        total,
      };
    })
    .sort((a, b) => b.total - a.total);

  const totalDistributionValue = distributionRows.reduce(
    (sum, row) => sum + row.total,
    0,
  );
  const peakDistributionValue = Math.max(
    ...distributionRows.map((row) => row.total),
    0,
  );

  const trendMax = Math.max(...trendSeries, 0);
  const trendTop = trendMax > 0 ? Math.ceil(trendMax * 1.2) : 100;
  const trendTicks = Array.from({ length: 5 }, (_, i) =>
    Math.round((trendTop * (4 - i)) / 4),
  );

  const chartWidth = 680;
  const chartHeight = 250;
  const leftPadding = 26;
  const topPadding = 18;
  const bottomPadding = 12;
  const usableWidth = chartWidth - leftPadding * 2;
  const usableHeight = chartHeight - topPadding - bottomPadding;

  const pointCoords = trendSeries.map((value, index) => {
    const x =
      leftPadding +
      (trendSeries.length > 1
        ? (index / (trendSeries.length - 1)) * usableWidth
        : usableWidth / 2);
    const y =
      topPadding +
      usableHeight -
      (trendTop > 0 ? (value / trendTop) * usableHeight : 0);

    return { x, y, value };
  });

  const trendHoverZones = pointCoords.map((point, index) => {
    const previousPoint = pointCoords[index - 1];
    const nextPoint = pointCoords[index + 1];

    const xStart = index === 0 ? leftPadding : (previousPoint.x + point.x) / 2;
    const xEnd =
      index === pointCoords.length - 1
        ? chartWidth - leftPadding
        : (point.x + nextPoint.x) / 2;

    return {
      index,
      x: xStart,
      width: Math.max(0, xEnd - xStart),
    };
  });

  const activeTrendIndex =
    hoveredTrendIndex != null &&
    hoveredTrendIndex >= 0 &&
    hoveredTrendIndex < pointCoords.length
      ? hoveredTrendIndex
      : null;

  const hoveredTrendPoint =
    activeTrendIndex != null ? pointCoords[activeTrendIndex] : null;
  const hoveredTrendDay =
    activeTrendIndex != null ? visibleDays[activeTrendIndex] : null;
  const hoveredTrendMonth =
    hoveredTrendDay != null
      ? `${hoveredTrendDay} ${chartMonthsLong[selectedMonthIndex]} ${selectedYear}`
      : "";
  const hoveredTrendAmount =
    activeTrendIndex != null ? trendSeries[activeTrendIndex] || 0 : 0;
  const isTooltipRightSide =
    hoveredTrendPoint != null && hoveredTrendPoint.x < chartWidth * 0.38;

  const trendLinePath = pointCoords
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const incomeSummaryTotal = trendSeries.reduce((sum, value) => sum + value, 0);
  const incomeAverage = trendSeries.length
    ? Math.round(incomeSummaryTotal / trendSeries.length)
    : 0;

  const selectedCardCode = (selectedIncomeCard?.title || "Income")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 4)
    .toUpperCase();

  const buildTransactionBreakdown = (amount) => {
    const base = {
      rent: Math.round(amount * 0.6),
      water: Math.round(amount * 0.14),
      garbage: Math.round(amount * 0.08),
      serviceFee: Math.round(amount * 0.1),
      arrears: Math.round(amount * 0.06),
      deposit: Math.round(amount * 0.02),
    };

    if (selectedIncomeCard.title === "Rent Collected") {
      return {
        rent: amount,
        water: 0,
        garbage: 0,
        serviceFee: 0,
        arrears: 0,
        deposit: 0,
      };
    }

    if (selectedIncomeCard.title === "Water Paid") {
      return {
        rent: 0,
        water: amount,
        garbage: 0,
        serviceFee: 0,
        arrears: 0,
        deposit: 0,
      };
    }

    if (selectedIncomeCard.title === "Garbage Paid") {
      return {
        rent: 0,
        water: 0,
        garbage: amount,
        serviceFee: 0,
        arrears: 0,
        deposit: 0,
      };
    }

    if (selectedIncomeCard.title === "Arrears Paid") {
      return {
        rent: 0,
        water: 0,
        garbage: 0,
        serviceFee: 0,
        arrears: amount,
        deposit: 0,
      };
    }

    if (selectedIncomeCard.title === "Deposit Paid") {
      return {
        rent: 0,
        water: 0,
        garbage: 0,
        serviceFee: 0,
        arrears: 0,
        deposit: amount,
      };
    }

    return base;
  };

  const transactionRows = visibleApartmentSeries
    .map((apartment, apartmentIndex) => {
      const value = apartment.monthly[0] || 0;
      const day = 5 + ((apartmentIndex + selectedMonthIndex) % 22);
      const houseNo = `H-${100 + apartmentIndex * 12 + selectedMonthIndex + 1}`;
      const approvalStatus = value >= incomeAverage ? "Completed" : "Pending";
      const breakdown = buildTransactionBreakdown(value);

      return {
        id: `${apartment.name}-${selectedYear}-${selectedMonthIndex}`,
        apartment: apartment.name,
        houseNo,
        date: `${day} ${chartMonthsLong[selectedMonthIndex]} ${selectedYear}`,
        approvalStatus,
        paymentReference: `${selectedCardCode}-${String(selectedMonthIndex + 1).padStart(2, "0")}${String(apartmentIndex + 1).padStart(2, "0")}${String(selectedYear).slice(-2)}`,
        monthLabel: chartMonths[selectedMonthIndex],
        amount: value,
        breakdown,
      };
    })
    .filter(
      (row) =>
        activeMockApartment === "All apartments" ||
        row.apartment === activeMockApartment,
    )
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);

  const handleTransactionClick = (transactionId) => {
    setExpandedTransaction((previous) =>
      previous === transactionId ? null : transactionId,
    );
  };

  return (
    <div className="income-details-page">
      <div className="income-details-header">
        <div className="income-header-left">
          <div className="income-title-wrap">
            <h2>{selectedIncomeCard.title}</h2>
            <p>Collection trends and apartment split for this income type.</p>
          </div>
        </div>

        <div className="income-header-right">
          <div className="filter-item">
            <DropdownSelect
              choices={chartMonthsLong}
              defaultChoice={chartMonthsLong[selectedMonthIndex]}
              onSelect={setIncomeDetailMonth}
              borderRadius={"25px"}
              showIcon={true}
              padding={"5px 7px"}
              backgroundColor={"#ffffff"}
            />
          </div>

          <div className="filter-item">
            <DropdownSelect
              choices={yearChoices}
              defaultChoice={selectedYear}
              onSelect={(year) => setIncomeDetailYear(String(year))}
              borderRadius={"25px"}
              showIcon={true}
              padding={"5px 7px"}
              backgroundColor={"#ffffff"}
            />
          </div>

          <div className="filter-item">
            <DropdownSelect
              choices={propertyDropdownChoices}
              defaultChoice={selectedIncomeApartment || "All properties"}
              onSelect={setSelectedIncomeApartment}
              borderRadius={"25px"}
              showIcon={true}
              padding={"5px 7px"}
              backgroundColor={"#ffffff"}
            />
          </div>

          <button type="button" className="income-export-button" onClick={handleExportMonthly}>
            <IoIosArrowRoundDown className="icon" />
            Export
          </button>
        </div>
      </div>

      <div className="income-kpi-strip">
        <div className="kpi-card">
          <span className="kpi-label">Total Collected</span>
          <strong>{formatAmount(incomeSummaryTotal)}</strong>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Monthly Average</span>
          <strong>{formatAmount(incomeAverage)}</strong>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Best Apartment</span>
          <strong>{distributionRows[0]?.apartment || "-"}</strong>
        </div>
      </div>

      <div className="income-details-graphs">
        <div className="graph-card trend">
          <div className="graph-head">
            <h3>Collected Over Time</h3>
            <span>{selectedPeriodLabel}</span>
          </div>

          <div className="trend-chart-wrap">
            <div className="trend-plot-row">
              <div className="trend-y-axis">
                {trendTicks.map((tick, index) => {
                  const y =
                    topPadding +
                    usableHeight -
                    (trendTop > 0 ? (tick / trendTop) * usableHeight : 0);

                  return (
                    <span
                      key={`${tick}-${index}`}
                      style={{ top: `${(y / chartHeight) * 100}%` }}
                    >
                      {formatShortAmount(tick)}
                    </span>
                  );
                })}
              </div>

              <div className="trend-chart-canvas">
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  role="img"
                  aria-label={`${selectedIncomeCard.title} trend chart`}
                  onMouseLeave={() => setHoveredTrendIndex(null)}
                >
                  {trendTicks.map((tick, index) => {
                    const y =
                      topPadding +
                      usableHeight -
                      (trendTop > 0 ? (tick / trendTop) * usableHeight : 0);

                    return (
                      <line
                        key={`${tick}-${index}`}
                        x1={leftPadding}
                        x2={chartWidth - leftPadding}
                        y1={y}
                        y2={y}
                        stroke="rgba(13, 76, 203, 0.15)"
                        strokeDasharray="4 4"
                        strokeWidth="1"
                      />
                    );
                  })}

                  {trendLinePath ? (
                    <path
                      d={trendLinePath}
                      fill="none"
                      stroke="#B39359"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : null}

                  {hoveredTrendPoint ? (
                    <line
                      x1={hoveredTrendPoint.x}
                      x2={hoveredTrendPoint.x}
                      y1={topPadding}
                      y2={chartHeight - bottomPadding}
                      stroke="rgba(25, 42, 58, 0.25)"
                      strokeDasharray="2 2"
                      strokeWidth="1"
                    />
                  ) : null}

                  {hoveredTrendPoint ? (
                    <circle
                      cx={hoveredTrendPoint.x}
                      cy={hoveredTrendPoint.y}
                      r="5"
                      fill="#B39359"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  ) : null}

                  {trendHoverZones.map((zone) => (
                    <rect
                      key={`trend-hover-zone-${zone.index}`}
                      x={zone.x}
                      y={topPadding}
                      width={zone.width}
                      height={usableHeight}
                      fill="transparent"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={() => setHoveredTrendIndex(zone.index)}
                      onMouseMove={() => setHoveredTrendIndex(zone.index)}
                    />
                  ))}
                </svg>

                {hoveredTrendPoint ? (
                  <div
                    className={`trend-tooltip ${isTooltipRightSide ? "side-right" : "side-left"}`}
                    style={{
                      left: `${(hoveredTrendPoint.x / chartWidth) * 100}%`,
                      top: `${(hoveredTrendPoint.y / chartHeight) * 100}%`,
                    }}
                  >
                    <div className="tooltip-month">{hoveredTrendMonth}</div>
                    <div className="tooltip-row">
                      <span className="tooltip-dot" />
                      <div className="tooltip-values">
                        <div>
                          {selectedIncomeCard.title}:{" "}
                          {formatAmount(hoveredTrendAmount)}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className="trend-x-axis"
              style={{
                gridTemplateColumns: `repeat(${Math.max(visibleDays.length, 1)}, minmax(0, 1fr))`,
              }}
            >
              {weekScaleLabels.map((week) => (
                <span
                  key={week.key}
                  style={{ gridColumn: `span ${week.span}` }}
                  title={`${chartMonthsLong[selectedMonthIndex]} ${week.startDay}-${week.endDay}`}
                >
                  {week.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="graph-card distribution">
          <div className="graph-head">
            <h3>Distribution Across Apartments</h3>
            <span>{selectedPeriodLabel}</span>
          </div>

          <div className="distribution-list">
            {distributionRows.map((row) => {
              const width =
                peakDistributionValue > 0
                  ? Math.max(8, (row.total / peakDistributionValue) * 100)
                  : 8;
              const percent =
                totalDistributionValue > 0
                  ? Math.round((row.total / totalDistributionValue) * 100)
                  : 0;

              return (
                <div key={row.apartment} className="distribution-row">
                  <div className="distribution-meta">
                    <span className="apartment-name">{row.apartment}</span>
                    <span className="apartment-amount">
                      {formatAmount(row.total)} ({percent}%)
                    </span>
                  </div>

                  <div className="distribution-track">
                    <div
                      className="distribution-fill"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="report-table-title">Latest Transactions</div>
      <div className="table-section">
        <div className="table-wrapper">
          <table className="income-transactions-table header-only">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Apartment</th>
                <th>House Number</th>
                <th>Date</th>
                <th>Approval Status</th>
                <th>Payment Reference</th>
                <th>Total Amount</th>
              </tr>
            </thead>
          </table>

          <div className="table-body-wrapper">
            <table className="income-transactions-table">
              <tbody>
                {transactionRows.map((tx) => (
                  <React.Fragment key={tx.id}>
                    <tr
                      className="clickable-row"
                      onClick={() => handleTransactionClick(tx.id)}
                    >
                      <td style={{ textAlign: "left" }}>{tx.apartment}</td>
                      <td>{tx.houseNo}</td>
                      <td>{tx.date}</td>
                      <td
                        className={`dashboard-status ${
                          tx.approvalStatus === "Pending"
                            ? "pending"
                            : "completed"
                        }`}
                      >
                        <div className="status-wrapper">
                          {tx.approvalStatus}
                        </div>
                      </td>
                      <td>{tx.paymentReference}</td>
                      <td>{formatAmount(tx.amount)}</td>
                    </tr>

                    {expandedTransaction === tx.id && (
                      <tr className="expanded-row">
                        <td colSpan="6">
                          <div className="expanded-content">
                            <div className="title">Transaction Details</div>
                            <div className="data">
                              <div className="head">Income Type:</div>
                              <div className="data-details">
                                {selectedIncomeCard.title}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Payment Reference:</div>
                              <div className="data-details">
                                {tx.paymentReference}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Apartment:</div>
                              <div className="data-details">{tx.apartment}</div>
                            </div>
                            <div className="data">
                              <div className="head">House Number:</div>
                              <div className="data-details">{tx.houseNo}</div>
                            </div>
                            <div className="data">
                              <div className="head">Month:</div>
                              <div className="data-details">
                                {tx.monthLabel}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Rent:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.rent)}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Water:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.water)}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Garbage:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.garbage)}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Service Fee:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.serviceFee)}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Arrears:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.arrears)}
                              </div>
                            </div>
                            <div className="data">
                              <div className="head">Deposit:</div>
                              <div className="data-details">
                                {formatAmount(tx.breakdown.deposit)}
                              </div>
                            </div>
                            <div className="data final">
                              <div className="head">Total Amount:</div>
                              <div className="total">
                                {formatAmount(tx.amount)}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
